// Shared time helpers. Hub People renders every timestamp in the VISITOR'S
// local timezone (Mitchell ask 2026-04-26: "make all time shown on anything
// the local time based off ip or something"). The visitor's browser already
// knows their tz from the OS — that's more accurate than IP geo (VPN /
// mobile-network / NAT all break IP-based tz lookups), so we use it.
//
// Server stores UTC ISO 8601; browser converts. We omit the `timeZone`
// option from Intl.DateTimeFormat so it defaults to
// `Intl.DateTimeFormat().resolvedOptions().timeZone` — i.e. the visitor's
// local. Capacitor WebView reports the device tz correctly on both
// Android and iOS, so this works for the mobile app too.
//
// HUB_TZ remains exported for the rare cases that need a specific tz
// (server-side renders, scheduled-job day-bucketing where "today" must
// be Mitchell-relative not visitor-relative). New code should NOT pass
// it to Intl.DateTimeFormat unless that exact constraint applies.

export const HUB_TZ = 'Australia/Sydney';
export const HUB_LOCALE = 'en-AU';

/** The visitor's local timezone (resolved by the JS engine). Used for the
 *  optional `tzLabel()` chip we hang next to important times. */
export function visitorTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/** Short tz abbrev for the visitor (e.g. "AEST", "PST", "GMT+1"). For the
 *  small grey chip rendered next to bookings / scheduled events so the user
 *  knows the time is in their local zone. Falls back to the IANA name on
 *  browsers that don't surface the abbrev. */
export function visitorTimeZoneAbbrev(at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'short',
    }).formatToParts(at);
    const tz = parts.find(p => p.type === 'timeZoneName')?.value;
    return tz || visitorTimeZone();
  } catch {
    return visitorTimeZone();
  }
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Short time, e.g. "3:31 pm" — for same-day timestamps. */
export function fmtTime(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleTimeString(HUB_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Short date, e.g. "22 Apr". Cross-day summary. */
export function fmtDate(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleDateString(HUB_LOCALE, {
    day: 'numeric',
    month: 'short',
  });
}

/** Calls-style adaptive: today = time, <7 days = weekday, else full date. */
export function fmtCallTime(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return '';
  const nowParts = new Intl.DateTimeFormat(HUB_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date());
  const thenParts = new Intl.DateTimeFormat(HUB_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
  if (nowParts === thenParts) {
    return fmtTime(iso);
  }
  const daysAgo = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (daysAgo < 7) {
    return d.toLocaleDateString(HUB_LOCALE, { weekday: 'short' });
  }
  return fmtDate(iso);
}

/** Discord-style chat/DM/thread per-message header timestamp. Three buckets:
 *  - today      → "Today at 3:31 pm"
 *  - yesterday  → "Yesterday at 3:31 pm"
 *  - older      → "DD/MM/YYYY 3:31 pm" (locale numeric short, leading zeros)
 *  Day-bucket comparison is done in the visitor's local tz (Mitchell ask
 *  2026-04-26 — "show times in user's local time"). Older bucket uses
 *  2-digit day/month for width stability + Discord parity. Single source of
 *  truth for chat.ts, threads.ts, and pages/dms.ts. */
export function fmtMessageHeaderTime(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return '';
  const dayKey = (date: Date) => new Intl.DateTimeFormat(HUB_LOCALE, {
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);
  const todayKey = dayKey(new Date());
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = dayKey(yesterday);
  const thenKey = dayKey(d);
  const time = fmtTime(iso);
  if (thenKey === todayKey) return `Today at ${time}`;
  if (thenKey === yesterdayKey) return `Yesterday at ${time}`;
  return `${d.toLocaleDateString(HUB_LOCALE, {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })} ${time}`;
}

/** Chat-style: time if today, date+time if older. */
export function fmtChatTime(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return '';
  const nowDay = new Intl.DateTimeFormat(HUB_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date());
  const thenDay = new Intl.DateTimeFormat(HUB_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
  if (nowDay === thenDay) return fmtTime(iso);
  return `${fmtDate(iso)} ${fmtTime(iso)}`;
}

/** Full date+time, e.g. "Wednesday, 22 April 2026, 3:31 pm" — for tooltips. */
export function fmtFull(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleString(HUB_LOCALE, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Day heading, e.g. "Wednesday, 22 April 2026". */
export function fmtDayHeading(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleDateString(HUB_LOCALE, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Relative "Xm ago" / "2h ago" / "Mon" / "22 Apr" for email-inbox style. */
export function fmtRelative(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return '';
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return d.toLocaleDateString(HUB_LOCALE, { weekday: 'short' });
  return fmtDate(iso);
}
